/**
 * NumberCell — NUMBER-variant editable cell. Accepts optional `isDecimal` to
 * allow a single decimal point with up to two trailing digits.
 */

import { useEffect, useState } from 'react';
import type { KeyboardEvent } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { validateNumberKey } from '../../utils/cellValidation';

import CellShell from './CellShell';
import type { BaseCellProps } from './types';
import { commitInputChange, hydrateFromValue } from './inputCellBehavior';

interface NumberCellProps extends BaseCellProps {
  isDecimal?: boolean;
  maxCount?: number;
}

const NumberCell = ({
  value,
  id,
  name,
  isActive,
  isDisabled = false,
  isOptionalField = false,
  errorMessage,
  setData,
  isDecimal = false,
  maxCount,
}: NumberCellProps) => {
  const [inputText, setInputText] = useState(() => hydrateFromValue(value));

  useEffect(() => {
    setInputText(hydrateFromValue(value));
  }, [value]);

  const handleBlur = (inputValue: string): void => {
    commitInputChange({
      inputValue,
      originalValue: value,
      id,
      name,
      isActive,
      errorMessage,
      isOptionalField,
      setData,
    });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    validateNumberKey(e, isDecimal);
  };

  return (
    <CellShell
      isActive={isActive}
      isOptionalField={isOptionalField}
      errorMessage={errorMessage}
      value={value}
    >
      <Input
        id="input-value"
        type="number"
        aria-describedby="input-value"
        name="input"
        placeholder={value === '-' ? '-' : value === null ? '-' : undefined}
        disabled={isDisabled}
        value={inputText}
        maxLength={maxCount}
        className={cn(isActive && 'border-destructive')}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={(e) => handleBlur(e.target.value)}
        onKeyDown={handleKeyDown}
        data-testid="input-value_id"
      />
    </CellShell>
  );
};

export default NumberCell;
